const DEFAULT_PREVIEW_WIDTH = 768;
const MIN_RETRY_INTERVAL = 250;

/**
 * Creates page images and manages progressive page-asset loading.
 */
export default class PageAssets {
  #eventCleanups = [];

  #retryTimers = new Set();

  #clock;

  #createPreloadImage;

  constructor({
    root,
    info,
    i18n,
    clock = globalThis,
    createPreloadImage = (documentRoot) => documentRoot.createElement("img"),
  }) {
    this.root = root;
    this.info = info;
    this.i18n = i18n;
    this.#clock = clock;
    this.#createPreloadImage = createPreloadImage;
  }

  createImage(pageInfo, index, thumbnail = false) {
    const firstPageIndex = Object.keys(this.info.pageArray)[0];
    const initialPageIndex = this.root.dataset.initialPage;
    const isInitialPage =
      !thumbnail &&
      String(index) === String(initialPageIndex ?? firstPageIndex);
    let image = isInitialPage
      ? this.root.querySelector(":scope > .rhea-initial-page")
      : null;
    if (image && !this.initialPagePreview) {
      this.initialPagePreview = image.cloneNode(true);
      this.initialPagePreview.classList.add("rhea-initial-page-preview");
      this.initialPagePreview.setAttribute("aria-hidden", "true");
      this.initialPagePreview.alt = "";
      this.root.prepend(this.initialPagePreview);
    }
    image ??= this.root.ownerDocument.createElement("img");

    const urls = this.#urls(index);
    const source = thumbnail ? urls.thumbnail : urls.preview;
    if (!thumbnail) {
      image.classList.remove("rhea-initial-page");
      image.classList.add("img-fluid");
      image.width = pageInfo.dimensions.vWidth;
      image.height = pageInfo.dimensions.vHeight;
      image.style.aspectRatio = String(
        pageInfo.dimensions.vWidth / pageInfo.dimensions.vHeight,
      );
      image.dataset.rheaFullsize = urls.fullsize;
    }

    const label = `${thumbnail ? this.i18n.toPage : this.i18n.page} ${index}`;
    image.alt = label;
    if (thumbnail) image.title = label;

    this.#enableRetry(image, source);

    // Let the HTML parser request the initial page before the viewer chunk is
    // available. Splide lazy-loads every page after it.
    if (thumbnail) {
      image.src = source;
    } else if (isInitialPage) {
      image.src = source;
      image.fetchPriority = "high";
      image.decoding = "async";
      this.initialPageImage = image;
    } else {
      image.dataset.splideLazy = source;
    }

    return image;
  }

  upgradeVisible(pagesList, index, doublePages = false) {
    const visibleSlideIndexes = [index];
    if (doublePages) visibleSlideIndexes.push(index + 1);

    for (const slideIndex of visibleSlideIndexes) {
      const image = pagesList.children[slideIndex]?.querySelector(
        "img[data-rhea-fullsize]",
      );
      if (!image) continue;
      const fullsizeUrl = image.dataset.rheaFullsize;
      if (!fullsizeUrl || image.dataset.rheaFullsizeLoaded === "true") continue;

      const preload = this.#createPreloadImage(image.ownerDocument);
      const swap = () => {
        if (!image.isConnected || image.dataset.rheaFullsizeLoaded === "true") {
          return;
        }
        image.src = fullsizeUrl;
        image.dataset.rheaFullsizeLoaded = "true";
      };
      const load = () => {
        if (typeof preload.decode !== "function") {
          swap();
          return;
        }
        preload.decode().then(swap).catch(swap);
      };
      this.#listen(preload, "load", load, { once: true });
      preload.src = fullsizeUrl;
    }
  }

  removeInitialPreview() {
    this.initialPagePreview?.remove();
    this.initialPagePreview = undefined;
  }

  destroy() {
    this.removeInitialPreview();
    for (const cleanup of this.#eventCleanups.splice(0).reverse()) cleanup();
    for (const timer of this.#retryTimers) this.#clock.clearTimeout(timer);
    this.#retryTimers.clear();
  }

  #urls(index) {
    const pageName = `page_${String(index).padStart(4, "0")}`;
    const pagesPath = `${this.info.path}pages/`;
    const configuredPreviewWidth = Number(this.info.previewWidth);
    const previewWidth =
      Number.isInteger(configuredPreviewWidth) && configuredPreviewWidth >= 320
        ? configuredPreviewWidth
        : DEFAULT_PREVIEW_WIDTH;

    return {
      thumbnail: `${this.info.path}thumbnails/${pageName}_thumb.webp`,
      preview: `${pagesPath}${pageName}_${previewWidth}.webp`,
      fullsize: `${pagesPath}${pageName}.webp`,
    };
  }

  #enableRetry(image, url) {
    if (!this.info.assetsPending) return;

    const configuredInterval = Number(this.info.assetRetryInterval);
    const configuredAttempts = Number(this.info.assetRetryAttempts);
    const interval = Number.isFinite(configuredInterval)
      ? Math.max(MIN_RETRY_INTERVAL, configuredInterval)
      : 2000;
    const maxAttempts = Number.isFinite(configuredAttempts)
      ? Math.max(0, configuredAttempts)
      : 900;
    let attempts = 0;
    let timer;

    const clearRetry = () => {
      if (timer === undefined) return;
      this.#clock.clearTimeout(timer);
      this.#retryTimers.delete(timer);
      timer = undefined;
    };
    const retry = () => {
      this.#retryTimers.delete(timer);
      timer = undefined;
      if (!image.isConnected || attempts >= maxAttempts) return;
      attempts += 1;
      const retryUrl = new URL(url, image.ownerDocument.baseURI);
      retryUrl.searchParams.set("rhea-retry", String(attempts));
      image.src = retryUrl.href;
    };
    const scheduleRetry = () => {
      clearRetry();
      if (attempts >= maxAttempts) return;
      timer = this.#clock.setTimeout(retry, interval);
      this.#retryTimers.add(timer);
    };

    this.#listen(image, "error", scheduleRetry);
    this.#listen(image, "load", clearRetry);
    this.#eventCleanups.push(clearRetry);
  }

  #listen(target, eventName, listener, options) {
    target.addEventListener(eventName, listener, options);
    this.#eventCleanups.push(() =>
      target.removeEventListener(eventName, listener, options),
    );
  }
}
